import type { Counter } from "./Counter";
import type { Board } from "./board";
import color from "./color.module.scss";
import type { RelativeCoordinate, WindowCoordinate } from "./coordinates";
import type { Icon } from "./icon";
import type { Art } from "./session";
import type { AuthUser } from "./user";

export interface Id {
  id: string;
}

export type IdMap<T> = Record<string, T>;

export type WorkingMode = "planning" | "execution";

// Design-system mapping: default→description (grey), ok→info (blue),
// warn→alert (yellow), error→warning (red). The in-app names are historical;
// the design spec uses the right-hand labels.
export type InfoLevel = "default" | "ok" | "warn" | "error";

export const infoLevelInfo: Record<InfoLevel, { color: string; backColor: string; icon: Icon }> = {
  default: {
    color: color.defaultInfoLevel,
    backColor: "var(--color-background-grey-light)",
    icon: "message/info",
  },
  ok: { color: color.ok, backColor: color.okBack, icon: "message/info" },
  warn: { color: color.warn, backColor: color.warnBack, icon: "message/warn" },
  error: { color: color.error, backColor: color.errorBack, icon: "message/error" },
};

// Our internal color type, representing [r, g, b, a], each on the range 0-1
export type Color = [number, number, number, number];

export type ArtId = number;

export const boardTypes = [
  "backlog",
  "program",
  "solution_backlog",
  "solution",
  "risk",
  "team",
  "flex",
  "objectives",
] as const;
export type BoardType = (typeof boardTypes)[number];

export type Level = "solution-train" | "art" | "team" | "none";

export function boardTypeToLevel(type: BoardType): Level {
  switch (type) {
    case "solution":
    case "solution_backlog":
      return "solution-train";
    case "program":
    case "backlog":
    case "objectives":
    case "risk":
      return "art";
    case "team":
      return "team";
    case "flex":
    default:
      return "none";
  }
}

const boardI18nKeys: Record<BoardType, { nav: string; short: string; long: string; icon: Icon }> = {
  backlog: {
    nav: /*$t*/ "board.backlog.nav",
    short: /*$t*/ "board.backlog",
    long: /*$t*/ "board.backlog.long",
    icon: "board/backlog",
  },
  program: {
    nav: /*$t*/ "board.program.nav",
    short: /*$t*/ "board.program",
    long: /*$t*/ "board.program.long",
    icon: "board/planning",
  },
  solution_backlog: {
    nav: /*$t*/ "board.backlog.nav",
    short: /*$t*/ "board.solutionBacklog",
    long: /*$t*/ "board.solutionBacklog.long",
    icon: "board/solution-backlog",
  },
  solution: {
    nav: /*$t*/ "board.solution.nav",
    short: /*$t*/ "board.solution",
    long: /*$t*/ "board.solution.long",
    icon: "board/solution-planning",
  },
  risk: {
    nav: /*$t*/ "board.risk.nav",
    short: /*$t*/ "board.risk",
    long: /*$t*/ "board.risk.long",
    icon: "board/risk",
  },
  team: {
    nav: /*$t*/ "board.team.nav",
    short: /*$t*/ "board.team",
    long: /*$t*/ "board.team.long",
    icon: "board/team",
  },
  flex: {
    nav: /*$t*/ "board.flex",
    short: /*$t*/ "board.flex",
    long: /*$t*/ "board.flex.long",
    icon: "board/canvases",
  },
  objectives: {
    nav: /*$t*/ "board.objectives.nav",
    short: /*$t*/ "board.objectives",
    long: /*$t*/ "board.objectives.long",
    icon: "board/objectives-art",
  },
};

export function boardTypeName(type: BoardType) {
  return boardI18nKeys[type].short;
}

export function boardNavName(type: BoardType) {
  return boardI18nKeys[type].nav;
}

export function boardIcon(type: BoardType) {
  return boardI18nKeys[type].icon;
}

export function boardTitle(board: Board, art?: Art | null): [string, Record<string, unknown>] {
  const key = boardI18nKeys[board.type];
  switch (board.type) {
    case "team":
      return [key.long, { team: board.team.name }];
    case "flex":
      return [key.long, { name: board.name }];
    default: {
      if (art === undefined) {
        return [key.short, {}];
      }
      const hasArt = board.artId && art?.name;
      return [key.long, { art: hasArt ? `(${art.name})` : "" }];
    }
  }
}

export function isBoardType(s: string): s is BoardType {
  return boardTypes.includes(s);
}

export const riskTypes = ["R", "O", "A", "M", ""] as const;
export type RiskType = (typeof riskTypes)[number];

const riskI18nKeys: Record<RiskType, { short: string; long: string; icon: Icon }> = {
  R: { short: /*$t*/ "risk.resolved.short", long: /*$t*/ "risk.resolved", icon: "risk/resolved" },
  O: { short: /*$t*/ "risk.owned.short", long: /*$t*/ "risk.owned", icon: "risk/owned" },
  A: { short: /*$t*/ "risk.accepted.short", long: /*$t*/ "risk.accepted", icon: "risk/accepted" },
  M: {
    short: /*$t*/ "risk.mitigated.short",
    long: /*$t*/ "risk.mitigated",
    icon: "risk/mitigated",
  },
  "": { short: /*$t*/ "risk.unRoam.short", long: /*$t*/ "risk.unRoam", icon: "risk/roam" },
};

export function riskTypeName(risk: RiskType) {
  return riskI18nKeys[risk].short;
}

export function riskTitle(risk: RiskType) {
  return riskI18nKeys[risk].long;
}

export function riskIcon(risk: RiskType) {
  return riskI18nKeys[risk].icon;
}

export const statusClasses = ["undefined", "todo", "in-progress", "done"] as const;
export type StatusClass = (typeof statusClasses)[number];

export const statusClassNames: Record<StatusClass, string> = {
  undefined: /*$t*/ "statusClass.unknown",
  todo: /*$t*/ "statusClass.toDo",
  "in-progress": /*$t*/ "statusClass.inProgress",
  done: /*$t*/ "statusClass.done",
};

export type AlmSourceId = number | string;

export type AlmType = "jira" | "ac" | "ado" | "";

export interface AlmTypeInfo {
  name: string;
  cockpitPath: string;
  icon: `alm/${Exclude<AlmType, "">}` | "system/minus";
  colorIcon: `alm/${Exclude<AlmType, "">}-color` | "system/minus";
  iterationName: `action.deleteAlmSticky.iterationName.${Exclude<AlmType, "">}` | "";
  isUserMapped: boolean;
  hasAssignee: boolean;
  hasKanban: boolean;
  hasIssueSelector: boolean;
  hasDevLinks: boolean;
}

export const almTypeInfo: Record<AlmType, AlmTypeInfo> = {
  jira: {
    name: "Jira",
    cockpitPath: "jira",
    icon: "alm/jira",
    colorIcon: "alm/jira-color",
    iterationName: /*$t*/ "action.deleteAlmSticky.iterationName.jira",
    isUserMapped: true,
    hasAssignee: true,
    hasKanban: true,
    hasIssueSelector: true,
    hasDevLinks: true,
  },
  ado: {
    name: "Azure DevOps",
    cockpitPath: "ado",
    icon: "alm/ado",
    colorIcon: "alm/ado-color",
    iterationName: /*$t*/ "action.deleteAlmSticky.iterationName.ado",
    isUserMapped: false,
    hasAssignee: false,
    hasKanban: false,
    hasIssueSelector: false,
    hasDevLinks: false,
  },
  ac: {
    name: "Rally",
    cockpitPath: "ac",
    icon: "alm/ac",
    colorIcon: "alm/ac-color",
    iterationName: /*$t*/ "action.deleteAlmSticky.iterationName.ac",
    isUserMapped: true,
    hasAssignee: true,
    hasKanban: false,
    hasIssueSelector: true,
    hasDevLinks: false,
  },
  "": {
    name: "",
    cockpitPath: "",
    icon: "system/minus",
    colorIcon: "system/minus",
    iterationName: "",
    isUserMapped: false,
    hasAssignee: true,
    hasKanban: false,
    hasIssueSelector: false,
    hasDevLinks: false,
  },
};

export interface Pointer {
  user: AuthUser;
  relativePos: RelativeCoordinate;
  windowPos: Array<{ pos: WindowCoordinate; index: number }>;
  timestamp: number;
}

export interface Statistics {
  calls: Counter;
  publishes: Counter;
  sends: Counter;
  receives: Counter;
  subscriptions: number;
  reconnects: number;
  storesLen: number;
}
